/**
 * Admin Orders Hub: native mobile app style.
 * Segmented control with 4 views: Calendar, Live, Cash Issue, Scan.
 * Each view fully replaces the content area.
 *
 * Uses AdminOrdersProvider for shared event context across all tabs.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconCalendar,
  IconDocument,
  IconQrCode,
  IconTicket,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import {
  AdminCashTicketsPage,
  TicketValidationPage,
} from "../tickets/index.ts";
import { OrdersCalendarView } from "./OrdersCalendarView.tsx";
import { OrdersLiveStream } from "./OrdersLiveStream.tsx";
import { AdminOrdersProvider } from "./context/index.ts";
import { EventContextHeader } from "./components/EventContextHeader.tsx";

type ViewMode = "calendar" | "live" | "cash" | "scan";

const VIEW_MODE_STORAGE_KEY = "admin_orders_view_mode";

/**
 * Main Admin Orders Page component.
 * Wrapped with AdminOrdersProvider for shared event context.
 */
export function AdminOrdersPage() {
  return (
    <AdminOrdersProvider>
      <AdminOrdersPageContent />
    </AdminOrdersProvider>
  );
}

/**
 * Inner content component that uses the context.
 */
function AdminOrdersPageContent() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { hasAnyPermission } = useAuth();

  // Permission checks
  const canIssueTickets = hasAnyPermission(["tickets.issue", "admin.full"]);
  const canScanTickets = hasAnyPermission(["tickets.scan", "admin.full"]);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    // Only restore calendar or live modes
    if (saved === "live") return "live";
    return "calendar";
  });

  // Save view mode (only calendar/live, not cash/scan)
  useEffect(() => {
    if (viewMode === "calendar" || viewMode === "live") {
      sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  // BackButton handling
  useEffect(() => {
    if (!tg) return;

    const handleBack = () => {
      // If in cash/scan mode, go back to previous orders view
      if (viewMode === "cash" || viewMode === "scan") {
        const saved = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
        setViewMode(saved === "live" ? "live" : "calendar");
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };

    tg.BackButton.show();
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, viewMode]);

  const handleModeChange = (mode: ViewMode) => {
    haptic("light");
    setViewMode(mode);
  };

  // Close panel and return to orders view
  const closePanel = () => {
    haptic("light");
    const saved = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    setViewMode(saved === "live" ? "live" : "calendar");
  };

  return (
    <Layout>
      {/* Event context header - visible on all tabs */}
      <EventContextHeader />

      {/* Segmented control */}
      <div className="flex items-center gap-2 mb-4">
        {/* Main tabs: Calendar / Live */}
        <div className="flex-1 flex p-1 bg-slate-100 rounded-xl">
          <button
            type="button"
            onClick={() => handleModeChange("calendar")}
            className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-[12px] font-medium transition-all ${
              viewMode === "calendar"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500"
            }`}
          >
            <IconCalendar className="w-3.5 h-3.5" />
            Календарь
          </button>
          <button
            type="button"
            onClick={() => handleModeChange("live")}
            className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-[12px] font-medium transition-all ${
              viewMode === "live"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500"
            }`}
          >
            <IconDocument className="w-3.5 h-3.5" />
            Лента
          </button>
        </div>

        {/* Quick action buttons - permission-based */}
        {canIssueTickets && (
          <button
            type="button"
            onClick={() => handleModeChange("cash")}
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all active:scale-95 ${
              viewMode === "cash"
                ? "bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300"
                : "bg-emerald-500 text-white"
            }`}
            aria-label="Выдать билеты за наличные"
          >
            <IconTicket className="w-5 h-5" />
          </button>
        )}
        {canScanTickets && (
          <button
            type="button"
            onClick={() => handleModeChange("scan")}
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all active:scale-95 ${
              viewMode === "scan"
                ? "bg-teal-600 text-white shadow-md ring-2 ring-teal-300"
                : "bg-teal-500 text-white"
            }`}
            aria-label="Сканировать билеты"
          >
            <IconQrCode className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content area - only one view at a time */}
      {viewMode === "calendar" && <OrdersCalendarView />}
      {viewMode === "live" && <OrdersLiveStream />}
      {viewMode === "cash" && (
        <AdminCashTicketsPage embedded onClose={closePanel} />
      )}
      {viewMode === "scan" && (
        <TicketValidationPage embedded onClose={closePanel} />
      )}

      <div className="h-4" />
    </Layout>
  );
}
